import { useEffect, useId, useRef } from "react";

import useAssistantPanelStore from "../Layout/useStore";
import useIsAssistantPageMode from "./useIsAssistantPageMode";

const OVERLAY_ATTRIBUTE = "data-assistant-overlay";

type AssistantPanelOverlayDismissOptions = {
  isOpen: boolean;
  onClose: () => void;
  /** Set false when the trigger toggles itself, so its own handler still runs. */
  swallowTriggerPress?: boolean;
};

/**
 * Light-dismiss for overlays inside the assistant panel. React-aria can't do it here: the
 * panel's `data-react-aria-top-layer` makes it ignore in-panel clicks, and `usePress` stops
 * propagation — hence document capture listeners.
 *
 * Put the trigger inside `wrapperRef`, spread `overlayProps` on the portaled overlay.
 */
const useAssistantPanelOverlayDismiss = ({
  isOpen,
  onClose,
  swallowTriggerPress = true,
}: AssistantPanelOverlayDismissOptions) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const overlayId = useId();
  const isPanelOpen = useAssistantPanelStore((state) => state.isOpen);
  const isPageMode = useIsAssistantPageMode();

  // Closing in render is safe — <Activity> defers hidden-subtree updates until the panel returns.
  if (isOpen && !isPanelOpen && !isPageMode) {
    onClose();
  }

  const optionsRef = useRef({ isOpen, onClose, swallowTriggerPress });
  useEffect(() => {
    optionsRef.current = { isOpen, onClose, swallowTriggerPress };
  });

  useEffect(() => {
    const isInsideOverlay = (target: Element) =>
      !!target.closest(`[${OVERLAY_ATTRIBUTE}="${overlayId}"]`);

    const handlePress = (event: Event) => {
      const {
        isOpen: isOverlayOpen,
        onClose: close,
        swallowTriggerPress: swallow,
      } = optionsRef.current;
      const target = event.target;

      if (!isOverlayOpen || !(target instanceof Element) || isInsideOverlay(target)) {
        return;
      }

      if (wrapperRef.current?.contains(target)) {
        // React-aria triggers only open on press, so a re-press has to be swallowed.
        if (swallow) {
          event.stopPropagation();
          close();
        }
        return;
      }

      close();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (optionsRef.current.isOpen && event.key === "Escape") {
        optionsRef.current.onClose();
      }
    };

    // The same event react-aria drives off, so there is one call per press.
    const pressEvent = typeof PointerEvent !== "undefined" ? "pointerdown" : "mousedown";

    document.addEventListener(pressEvent, handlePress, { capture: true });
    document.addEventListener("keydown", handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener(pressEvent, handlePress, { capture: true });
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [overlayId]);

  return { wrapperRef, overlayProps: { [OVERLAY_ATTRIBUTE]: overlayId } };
};

export default useAssistantPanelOverlayDismiss;
